import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type FiltersState, initialFiltersState } from '../constants';
import { areFiltersEqual, filtersToSearchParams, searchParamsToFilters } from '../utils';

type EventsFiltersContextType = {
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  resetFilters: () => void;
  areFiltersDefault: boolean;
};

const EventsFiltersContext = createContext<EventsFiltersContextType | undefined>(undefined);

export const EventsFiltersProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFiltersState] = useState<FiltersState>(() => {
    return searchParamsToFilters(searchParams, initialFiltersState);
  });
  const filtersRef = useRef(filters);
  const isUpdatingFromCode = useRef(false);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const setFilters = (newFilters: React.SetStateAction<FiltersState>) => {
    isUpdatingFromCode.current = true;
    setFiltersState((prevFilters) => {
      const updatedFilters = typeof newFilters === 'function' ? newFilters(prevFilters) : newFilters;
      const paramsFromFilters = filtersToSearchParams(updatedFilters);
      setSearchParams(paramsFromFilters, { replace: true });
      setTimeout(() => {
        isUpdatingFromCode.current = false;
      }, 0);
      return updatedFilters;
    });
  };

  useEffect(() => {
    if (isUpdatingFromCode.current) {
      return;
    }

    const filtersFromParams = searchParamsToFilters(searchParams, initialFiltersState);

    if (!areFiltersEqual(filtersRef.current, filtersFromParams)) {
      setFiltersState(filtersFromParams);
    }
  }, [searchParams]);

  const resetFilters = () => {
    isUpdatingFromCode.current = true;
    setFiltersState(initialFiltersState);
    setSearchParams(new URLSearchParams(), { replace: true });
    setTimeout(() => {
      isUpdatingFromCode.current = false;
    }, 0);
  };

  const areFiltersDefault = useMemo(() => areFiltersEqual(filters, initialFiltersState), [filters]);

  return (
    <EventsFiltersContext.Provider value={{ filters, setFilters, resetFilters, areFiltersDefault }}>
      {children}
    </EventsFiltersContext.Provider>
  );
};

export const useEventsFilters = () => {
  const context = useContext(EventsFiltersContext);
  if (context === undefined) {
    throw new Error('useEventsFilters must be used within EventsFiltersProvider');
  }
  return context;
};
